import type { BranchKey } from '@festina-life/api'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useLazyFetchBulkConfigurationPackage } from '../api/changesets/useLazyFetchBulkConfigurationPackage'
import { usePublishBranchChangesets } from '../api/changesets/usePublishBranchChangesets'
import {
  fetchServerTenantsKey,
  fetchStandardComponentConcisePackageKey,
} from '../api/keys'

export const usePublishChangesetsForBranch = () => {
  const {
    mutateAsync: fetchBulkConfigurationPackage,
    isPending: isFetchingBulkConfigurationPackage,
  } = useLazyFetchBulkConfigurationPackage()
  const { mutateAsync: publishChangeset, isPending: isPublishing } =
    usePublishBranchChangesets()
  const queryClient = useQueryClient()

  const handlePublishBranchChangesets = useCallback(
    async ({ branchKey, block }: { branchKey: BranchKey; block: string }) => {
      const configurationPackage = await fetchBulkConfigurationPackage({
        branchName: branchKey.name,
        server: branchKey.server,
        shouldIncludeBaseChangesets: true,
        shouldIncludeExtrinsicParameterValues: false,
        squash: true,
        block,
      })

      if (configurationPackage) {
        await publishChangeset({
          configurationPackage,
          server: branchKey.server,
        })

        await queryClient.invalidateQueries({
          queryKey: fetchStandardComponentConcisePackageKey(branchKey.server),
        })
        await queryClient.invalidateQueries({
          queryKey: fetchServerTenantsKey(branchKey.server),
        })
      }
    },
    [fetchBulkConfigurationPackage, publishChangeset, queryClient],
  )

  return {
    mutateAsync: handlePublishBranchChangesets,
    isPending: isFetchingBulkConfigurationPackage || isPublishing,
  }
}
